import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Wallet, Award, Users, LifeBuoy, LogOut, Copy, Check, ChevronLeft, Coins, Menu, Link as LinkIcon, Unplug, Power, ExternalLink, Headphones } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useWallet } from "@/contexts/WalletContext";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { openTelegramSupport } from "@/components/TelegramFAB";
import { shortAddr } from "@/lib/web3";
import { toast } from "sonner";
import api from "@/lib/api";

const BOTTOM_NAV = [
  { to: "/dashboard",    label: "Dashboard",    icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/achievements", label: "Achievements", icon: Award,           testid: "nav-rank" },
  { to: "/team",         label: "Teams",        icon: Users,           testid: "nav-team" },
  { to: "/wallet",       label: "Wallet",       icon: Wallet,          testid: "nav-wallet" },
];

const SECONDARY = [
  { to: "/staking", label: "Staking", icon: Coins, testid: "nav-staking" },
];

export default function DashboardLayout() {
  const { user, logout, refreshUser, token } = useAuth();
  const wallet = useWallet();
  const navigate = useNavigate();
  const location = useLocation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (token) refreshUser();
    // eslint-disable-next-line
  }, [location.pathname]);

  const onCopy = async () => {
    if (!user?.address) return;
    await navigator.clipboard.writeText(user.address);
    setCopied(true);
    toast.success("Address copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleConnect = async () => {
    if (typeof window === "undefined" || !window.ethereum) {
      toast.error("No wallet detected. Please open this site in your wallet's in-app browser (MetaMask / Trust / SafePal / Binance).", { duration: 6000 });
      return;
    }
    try {
      // Auto-detect which wallet is injected
      const eth = window.ethereum;
      let type = "metamask";
      if (eth.isSafePal || eth.isSafePalWallet) type = "safepal";
      else if (eth.isTrust || eth.isTrustWallet) type = "trust";
      else if (eth.isBinance || eth.isBinanceWallet) type = "binance";
      else if (eth.isOkxWallet || eth.isOKExWallet) type = "okx";
      else if (eth.isBitKeep || eth.isBitget) type = "bitget";
      else if (eth.isCoinbaseWallet) type = "coinbase";

      const connResult = await wallet.connectInjected(type);
      const connectedAddr = connResult?.address || wallet.address;

      // Link wallet to current user account (for email-based users without wallet linked).
      // Always call link-wallet on connect so subsequent deposits work even if user
      // re-connects with a different wallet later.
      if (user && connectedAddr) {
        try {
          await api.post("/auth/link-wallet", { address: connectedAddr });
        } catch (e) {
          /* link is best-effort — wallet still works locally */
        }
      }
      toast.success("Wallet connected");
    } catch (e) {
      const msg = (e?.message || "").toLowerCase();
      if (msg.includes("observation") || msg.includes("watch")) {
        toast.error("Wallet is in Watch-only mode. Import with seed phrase to enable signing.", { duration: 7000 });
      } else if (msg.includes("user rejected")) {
        toast.error("Connection cancelled");
      } else {
        toast.error(e?.message || "Failed to connect wallet");
      }
    }
  };

  const handleDisconnect = () => {
    wallet.disconnect();
    logout();
    toast.success("Disconnected");
    navigate("/");
  };

  // FIX: wallet is "connected" as long as wallet.address is set —
  // user.address may be null for new email/password signups (no wallet linked yet).
  const isConnected = Boolean(wallet?.address);
  const displayAddr = wallet?.address || user?.address;

  return (
    <div className="min-h-screen flex bg-[var(--surface-0)] text-[var(--text-1)]">
      {/* Sidebar - desktop only */}
      <aside className="hidden md:flex flex-col border-r border-[var(--card3d-border)] bg-[var(--surface-1)] w-[240px]">
        <div className="h-16 flex items-center gap-2 px-4 border-b border-[var(--card3d-border)]">
          <Logo size={32} glow />
          <span className="font-semibold tracking-wide">SMART HUB</span>
        </div>
        <nav className="flex-1 py-4 px-2 space-y-1">
          {[...BOTTOM_NAV, ...SECONDARY].map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-[rgba(124,58,237,0.16)] text-[var(--purple-300)] border border-[rgba(124,58,237,0.35)]"
                      : "text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-white/5 border border-transparent"
                  }`
                }
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}

          {/* Admin Panel removed from user dashboard — admin access is via separate /control login */}

          {/* Support — opens Telegram 1-on-1 chat (username hidden inside helper) */}
          <button
            onClick={openTelegramSupport}
            data-testid="nav-support"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-white/5 border border-transparent transition-colors group"
            aria-label="Open Telegram support chat"
          >
            <Headphones size={18} className="text-[#29B6F6] transition-transform group-hover:scale-110" />
            <span className="flex-1 text-left">Support</span>
            <span
              className="text-[9px] mono uppercase tracking-[0.10em] px-1.5 py-0.5 rounded-md"
              style={{
                background: "rgba(41, 182, 246, 0.12)",
                color: "#4FC3F7",
                border: "1px solid rgba(41, 182, 246, 0.30)",
              }}
            >
              Telegram
            </span>
          </button>
        </nav>
        <div className="p-3 border-t border-[var(--card3d-border)]">
          <button onClick={handleLogout} data-testid="logout-button" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-white/5 transition-colors">
            <LogOut size={18} />
            <span>Disconnect</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 flex items-center justify-between gap-3 px-4 sm:px-6 border-b border-[var(--card3d-border)] bg-[var(--surface-1)] sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <div className="md:hidden flex items-center gap-2">
              <Logo size={32} glow />
              <span className="font-semibold tracking-wide text-sm">SMART HUB</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />

            {isConnected ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="topbar-wallet-badge"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--surface-2)] border border-[var(--card3d-border)] hover:opacity-90 transition-colors"
                  >
                    <span className="w-2 h-2 rounded-full bg-[var(--success)] shadow-[0_0_8px_rgba(34,197,94,0.7)]" />
                    <span className="mono text-xs">{shortAddr(displayAddr)}</span>
                    <ChevronLeft size={12} className="text-[var(--text-3)] -rotate-90" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56 bg-[var(--surface-2)] border border-[var(--card3d-border)] text-[var(--text-1)]"
                  data-testid="wallet-dropdown-menu"
                >
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Connected Wallet</DropdownMenuLabel>
                  <div className="px-2 pb-2">
                    <div className="mono text-xs break-all text-[var(--text-2)]">{displayAddr}</div>
                  </div>
                  <DropdownMenuSeparator className="bg-[var(--card3d-border)]" />
                  <DropdownMenuItem
                    onClick={onCopy}
                    className="cursor-pointer focus:bg-white/5"
                    data-testid="wallet-menu-copy"
                  >
                    {copied ? <Check size={14} className="mr-2 text-[var(--success)]" /> : <Copy size={14} className="mr-2" />}
                    {copied ? "Copied!" : "Copy address"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => window.open(`https://bscscan.com/address/${displayAddr}`, "_blank")}
                    className="cursor-pointer focus:bg-white/5"
                    data-testid="wallet-menu-bscscan"
                  >
                    <ExternalLink size={14} className="mr-2" />
                    View on BscScan
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[var(--card3d-border)]" />
                  <DropdownMenuItem
                    onClick={handleDisconnect}
                    className="cursor-pointer focus:bg-[rgba(239,68,68,0.10)] text-[var(--destructive)]"
                    data-testid="wallet-menu-disconnect"
                  >
                    <Power size={14} className="mr-2" />
                    Disconnect
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                data-testid="topbar-connect-button"
                onClick={handleConnect}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full font-semibold text-black text-xs hover:opacity-95 transition-opacity"
                style={{ background: "linear-gradient(135deg, #FFD36A 0%, #F3BA2F 100%)", boxShadow: "0 6px 14px -4px rgba(255,211,106,0.45)" }}
              >
                <LinkIcon size={12} />
                Connect
              </button>
            )}

            <Sheet>
              <SheetTrigger asChild>
                <button className="md:hidden w-9 h-9 rounded-lg bg-[var(--surface-2)] border border-white/10 flex items-center justify-center">
                  <Menu size={16} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[var(--surface-1)] border-white/10 text-[var(--text-1)] w-[260px]">
                <div className="flex items-center gap-2 mb-6 mt-2">
                  <Logo size={32} glow />
                  <span className="font-semibold">SMART HUB</span>
                </div>
                <nav className="space-y-1">
                  {SECONDARY.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink key={item.to} to={item.to} onClick={() => {}} className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${isActive ? "bg-[rgba(124,58,237,0.16)] text-[var(--purple-300)]" : "text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-white/5"}`}>
                        <Icon size={16} /> {item.label}
                      </NavLink>
                    );
                  })}

                  {/* Admin Panel removed — admin access is via /control login */}

                  {/* Support — opens Telegram 1-on-1 chat (username hidden in helper) */}
                  <button
                    onClick={openTelegramSupport}
                    data-testid="nav-support-mobile"
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-white/5 transition-colors group"
                    aria-label="Open Telegram support chat"
                  >
                    <Headphones size={16} className="text-[#29B6F6] transition-transform group-hover:scale-110" />
                    <span className="flex-1 text-left">Support</span>
                    <span
                      className="text-[9px] mono uppercase tracking-[0.10em] px-1.5 py-0.5 rounded-md"
                      style={{
                        background: "rgba(41, 182, 246, 0.12)",
                        color: "#4FC3F7",
                        border: "1px solid rgba(41, 182, 246, 0.30)",
                      }}
                    >
                      Telegram
                    </span>
                  </button>

                  <div className="h-px bg-white/10 my-3" />
                  <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-white/5">
                    <LogOut size={16} /> Disconnect
                  </button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24 md:pb-8 max-w-[1100px] w-full mx-auto">
          <Outlet />
        </main>

        {/* Mobile bottom nav - 4 tabs */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[var(--surface-1)] border-t border-[var(--card3d-border)] grid grid-cols-4 pb-[max(env(safe-area-inset-bottom),0px)]">
          {BOTTOM_NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`${item.testid}-mobile`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center py-2.5 text-[10px] gap-1 transition-colors ${
                    isActive ? "text-[var(--gold-400)]" : "text-[var(--text-3)]"
                  }`
                }
                style={({ isActive }) => isActive ? { color: "#FFD36A" } : {}}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
